import { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, LockSimple, Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { estimateTemplateContentCapacity } from '../../lib/v3ContentCapacity';
import DesignV3CapacityPreview from './DesignV3CapacityPreview';
import DesignV3ProjectPalette from './DesignV3ProjectPalette';
import DesignV3TemplateEditor from './DesignV3TemplateEditor';

const VALID_VERTICAL_ALIGNMENTS = new Set(['TOP_ANCHORED', 'CENTERED', 'BOTTOM_ANCHORED']);

function verticalAlignmentsFromTemplate(template) {
  const builder = template?.builder_style || {};
  const sourceSlot = template?.source_slot || '';
  const globalRoles = builder?.global_roles || {};
  const localRoles = sourceSlot ? builder?.archetype_overrides?.[sourceSlot]?.roles || {} : {};
  const semantics = new Set([...Object.keys(globalRoles), ...Object.keys(localRoles)]);
  const out = {};
  for (const semantic of semantics) {
    const value = localRoles?.[semantic]?.vertical_alignment || globalRoles?.[semantic]?.vertical_alignment;
    if (VALID_VERTICAL_ALIGNMENTS.has(value)) out[semantic] = value;
  }
  return out;
}

function applyVerticalAlignments(template, alignments = {}) {
  if (!template) return template;
  const sourceSlot = template.source_slot || '';
  let builder = { ...(template.builder_style || {}) };
  for (const [semantic, verticalAlignment] of Object.entries(alignments || {})) {
    if (!VALID_VERTICAL_ALIGNMENTS.has(verticalAlignment)) continue;
    if (sourceSlot) {
      const slot = builder?.archetype_overrides?.[sourceSlot] || {};
      builder = {
        ...builder,
        archetype_overrides: {
          ...(builder.archetype_overrides || {}),
          [sourceSlot]: {
            ...slot,
            roles: {
              ...(slot.roles || {}),
              [semantic]: { ...(slot.roles?.[semantic] || {}), vertical_alignment: verticalAlignment },
            },
          },
        },
      };
    } else {
      builder = {
        ...builder,
        global_roles: {
          ...(builder.global_roles || {}),
          [semantic]: { ...(builder.global_roles?.[semantic] || {}), vertical_alignment: verticalAlignment },
        },
      };
    }
  }
  return { ...template, builder_style: builder };
}

export default function DesignV3TemplateLibrary({ api, projectId, assetRevision = 0, assetsSidebar = null }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [densityByTemplate, setDensityByTemplate] = useState({});
  const [verticalAlignByTemplate, setVerticalAlignByTemplate] = useState({});
  const editorRef = useRef(null);
  const editorRootRef = useRef(null);

  const load = useCallback(async (keepId = '') => {
    setLoading(true);
    try {
      await api.get(`/projects/${projectId}/design/v3/references`);
      const { data } = await api.get(`/projects/${projectId}/design/v3/templates`);
      const list = data?.templates || [];
      setTemplates(list);
      const requestedId = keepId || selected?.id || '';
      const id = list.some(item => item.id === requestedId) ? requestedId : list[0]?.id;
      if (id) {
        const { data: full } = await api.get(`/projects/${projectId}/design/v3/templates/${id}`);
        setSelected(full);
        const savedPreset = full?.builder_style?.content_capacity?.preview_preset || full?.builder_style?.content_capacity?.preset;
        if (savedPreset) setDensityByTemplate(current => ({ ...current, [id]: current[id] || savedPreset }));
        const persistedVertical = verticalAlignmentsFromTemplate(full);
        setVerticalAlignByTemplate(current => ({ ...current, [id]: { ...persistedVertical, ...(current[id] || {}) } }));
      } else setSelected(null);
    } catch (error) { toast.error(error.response?.data?.detail || error.message); }
    finally { setLoading(false); }
  }, [api, projectId, selected?.id]);

  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const presetFor = template => densityByTemplate[template?.id]
    || template?.builder_style?.content_capacity?.preview_preset
    || template?.builder_style?.content_capacity?.preset
    || '50-80';

  const withCapacityProfile = (template, verticalOverrides = verticalAlignByTemplate[template?.id] || verticalAlignmentsFromTemplate(template)) => {
    const aligned = applyVerticalAlignments(template, verticalOverrides);
    const estimated = estimateTemplateContentCapacity(aligned);
    const profile = { ...estimated, preview_preset: presetFor(aligned) };
    return {
      ...aligned,
      builder_style: { ...(aligned?.builder_style || {}), content_capacity: profile },
    };
  };

  const persistTemplate = async (template, verticalOverrides) => {
    if (!template?.id) return template;
    const profiled = withCapacityProfile(template, verticalOverrides);
    const { data } = await api.put(`/projects/${projectId}/design/v3/templates/${template.id}`, {
      name: profiled.name,
      layout: profiled.layout,
      builder_style: profiled.builder_style,
      reference: profiled.reference || {},
    });
    return data;
  };

  const persistCapacity = persistTemplate;

  const open = async id => {
    if (id === selected?.id) return;
    try {
      if (selected?.id && editorRef.current?.save) {
        const saved = await editorRef.current.save({ silent: true });
        if (!saved) return;
        await persistCapacity(saved);
        toast.success('Template precedente salvato automaticamente');
      }
      const { data } = await api.get(`/projects/${projectId}/design/v3/templates/${id}`);
      setSelected(data);
      const savedPreset = data?.builder_style?.content_capacity?.preview_preset || data?.builder_style?.content_capacity?.preset;
      if (savedPreset) setDensityByTemplate(current => ({ ...current, [id]: current[id] || savedPreset }));
      const persistedVertical = verticalAlignmentsFromTemplate(data);
      setVerticalAlignByTemplate(current => ({ ...current, [id]: { ...persistedVertical, ...(current[id] || {}) } }));
    } catch (error) { toast.error(error.response?.data?.detail || error.message); }
  };

  const create = async duplicateFrom => {
    try {
      const source = templates.find(item => item.id === duplicateFrom);
      const { data } = await api.post(`/projects/${projectId}/design/v3/templates`, { duplicate_from: duplicateFrom || '', name: source ? `${source.name} copia` : 'Nuovo template' });
      await load(data.id); toast.success(source ? 'Template duplicato' : 'Template creato');
    } catch (error) { toast.error(error.response?.data?.detail || error.message); }
  };

  const remove = async id => {
    if (!window.confirm('Eliminare questo Template V3?')) return;
    try {
      await api.delete(`/projects/${projectId}/design/v3/templates/${id}`);
      setVerticalAlignByTemplate(current => { const next = { ...current }; delete next[id]; return next; });
      setSelected(null);
      await load();
      toast.success('Template eliminato');
    } catch (error) { toast.error(error.response?.data?.detail || error.message); }
  };

  const setVerticalAlign = async (semantic, verticalAlignment) => {
    if (!selected?.id || !VALID_VERTICAL_ALIGNMENTS.has(verticalAlignment)) return;
    const selectedAtStart = selected;
    const persisted = verticalAlignmentsFromTemplate(selectedAtStart);
    const previous = verticalAlignByTemplate[selectedAtStart.id] || persisted;
    const nextVertical = { ...previous, [semantic]: verticalAlignment };
    setVerticalAlignByTemplate(current => ({ ...current, [selectedAtStart.id]: nextVertical }));
    try {
      const liveTemplate = editorRef.current?.save
        ? await editorRef.current.save({ silent: true })
        : selectedAtStart;
      if (!liveTemplate) throw new Error('Impossibile salvare lo stato corrente del template');
      const aligned = applyVerticalAlignments(liveTemplate, nextVertical);
      const saved = await persistTemplate(aligned, nextVertical);
      setSelected(saved);
      setTemplates(current => current.map(item => item.id === saved.id ? { ...item, content_capacity: saved.builder_style?.content_capacity } : item));
      toast.success(`${semantic.toUpperCase()}: vertical align aggiornato`);
    } catch (error) {
      setVerticalAlignByTemplate(current => ({ ...current, [selectedAtStart.id]: previous }));
      setSelected(selectedAtStart);
      toast.error(error.response?.data?.detail || error.message || 'Vertical align non salvato');
    }
  };

  const capacity = selected ? estimateTemplateContentCapacity(selected) : null;
  const selectedPreset = selected ? presetFor(selected) : '50-80';
  const selectedVerticalAlignments = selected ? (verticalAlignByTemplate[selected.id] || verticalAlignmentsFromTemplate(selected)) : {};

  return <div className="card space-y-4" data-testid="design-v3-template-library">
    <div className={assetsSidebar ? 'grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start' : ''}>
      <div className="min-w-0 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div><p className="text-[10px] uppercase tracking-[.18em] text-[var(--accent-purple)] font-semibold">Template V3</p><h3 className="text-lg font-semibold mt-1">Libreria layout carousel</h3><p className="text-xs text-[var(--text-muted)] mt-1 max-w-2xl">Ogni template è un layout libero: duplicalo, modificalo e assegnalo a qualunque slide nel Carousel Studio.</p></div>
          <div className="flex items-center gap-2 flex-wrap">
            <button className="btn-gradient text-xs" onClick={() => create('')}><Plus size={13}/>Nuovo template</button>
          </div>
        </div>
        {loading ? <div className="text-xs text-[var(--text-muted)]">Carico template…</div> :
          <div className="flex gap-3 overflow-x-auto pb-2" data-testid="design-v3-template-strip">
            {templates.map(item => <div key={item.id} className={`relative shrink-0 w-[118px] rounded-xl border p-2 cursor-pointer ${selected?.id===item.id?'border-fuchsia-500 bg-fuchsia-500/10':'border-[var(--border-color)]'}`} onClick={()=>open(item.id)}>
              <div className="aspect-[4/5] rounded-lg overflow-hidden bg-black/30 flex items-center justify-center">{item.thumbnail_url?<img src={item.thumbnail_url} alt="" className="w-full h-full object-cover"/>:<MiniTemplate roles={item.roles} requiresImage={item.requires_image}/>}</div>
              <div className="flex items-center gap-1 mt-2 min-w-0"><div className="text-[10px] font-medium truncate" title={item.name}>{item.name}</div>{item.locked&&<LockSimple size={10} className="shrink-0 text-fuchsia-300" aria-label="Template obbligatorio"/>}</div>
              <div className="text-[8px] text-[var(--text-muted)]">{item.requires_image?'con foto':'senza foto'}{item.locked?' · sistema':''}</div>
              <div className="flex items-center justify-center gap-2 mt-2"><button className="btn-ghost !w-8 !h-8 !min-w-0 !p-0 justify-center" title="Duplica" onClick={e=>{e.stopPropagation();create(item.id);}}><Copy size={11}/></button>{item.locked?<span className="flex h-8 w-8 items-center justify-center text-fuchsia-300" title="Template obbligatorio"><LockSimple size={12}/></span>:<button className="btn-ghost !w-8 !h-8 !min-w-0 !p-0 justify-center text-rose-400" title="Elimina" onClick={e=>{e.stopPropagation();remove(item.id);}}><Trash size={11}/></button>}</div></div>)}
          </div>}
      </div>
      {assetsSidebar}
    </div>
    {!loading&&selected&&<div ref={editorRootRef} className="space-y-3">
      <DesignV3CapacityPreview
        template={selected}
        editorRootRef={editorRootRef}
        preset={selectedPreset}
        capacity={capacity}
        verticalAlignmentOverrides={selectedVerticalAlignments}
        onPresetChange={preset=>setDensityByTemplate(current=>({...current,[selected.id]:preset}))}
        onVerticalAlign={setVerticalAlign}
      />
      <DesignV3ProjectPalette api={api} projectId={projectId} editorRootRef={editorRootRef} assetRevision={assetRevision}/>
      <DesignV3TemplateEditor ref={editorRef} api={api} projectId={projectId} template={selected} assetRevision={assetRevision} onSaved={async data=>{const profiled=await persistCapacity(data);setSelected(profiled);await load(profiled.id);}}/>
    </div>}
  </div>;
}

function MiniTemplate({ roles = [], requiresImage }) {
  return <div className="relative w-full h-full p-2">{requiresImage&&<div className="absolute inset-2 bg-white/10 rounded"/>}<div className="absolute left-3 right-3 bottom-5 space-y-1">{roles.slice(0,3).map((role,index)=><div key={`${role}-${index}`} className="h-1.5 rounded bg-white/50" style={{width:index===0?'80%':index===1?'60%':'90%'}}/>)}</div></div>;
}